import React, {useCallback, useEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {
  assertViewExists,
  getTitleForView,
  getViewForViewReference,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import {getDefaultChartConfig} from 'analysis-tool/ui/charting/ChartingUtils';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineStepConfig,
  PipelineViewGraphQL,
  assertPipelineStepIsStepType,
  pipelineStepIsStepType,
} from 'pipelines/PipelineTypes';
import {resolveLineageStep} from 'pipelines/PipelineViewUtils';
import {getPipelineStepRawDatasOfKind, getSingleInputRawData} from 'pipelines/StepDataUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../RightSidebarTypes';
import {saveChartOrTable} from '../utils/saveChartOrTable';
import ChartTypeSwitcher from './edit-view/ChartTypeSwitcher';
import {ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE} from './edit-view/chartTypeSwitchUtils';
import EditViewChart from './edit-view/EditViewChart';
import EditViewTransforms from './edit-view/EditViewTransforms';
import RightSidebarSkeleton, {
  RightSidebarWidth,
  RightSidebarConfirmationButton,
} from './RightSidebarSkeleton';
import useMaybeDraftStep, {draftStepGlobalState} from './useMaybeDraftStep';

export default function EditViewRightSidebar({
  analysis,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
  onChangeSelectedItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.EDIT_VIEW>): JSX.Element {
  const stepTypeInfos = useStepTypeInfos();
  const isDisabled = isAnalysisReadOnly(analysis) || isArchived(analysis);
  const view = getViewForViewReference(analysis, selectedRightSidebarItem.viewReference);
  assertViewExists(view, analysis, selectedRightSidebarItem.viewReference);

  const selectedViewNode = view.lineage.find(
    ({step}) => step.id === selectedRightSidebarItem.viewReference.stepId
  );
  assert(
    selectedViewNode != null,
    'Expected selectedRightSidebarItem.viewReference.stepId to correspond to a step in the analysis'
  );
  // Use type assertion for config access since the resolved step has a generic config type
  const originalStep = resolveLineageStep(analysis.steps, selectedViewNode) as PipelineStep & {
    config: Record<string, unknown> | null;
  };
  const {draftStep, setDraftStep} = useMaybeDraftStep(originalStep.id, null);

  // getDefaultClientIdForView(view) changes when transforms are added, so we need to use the original step id
  const clientStateId = originalStep.id;

  const lastNode = view.lineage[view.lineage.length - 1];
  const lastNodeStep = resolveLineageStep(analysis.steps, lastNode);
  const dataFrames = isViewTerminalStepType(lastNodeStep.stepType)
    ? getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: lastNodeStep.inputStepDatas.flatMap(({rawDatas}) => rawDatas),
      })
    : getPipelineStepRawDatasOfKind(PipelineStepDataKind.DATASET, {
        rawDatas: lastNodeStep.outputStepDatas.flatMap(({rawDatas}) => rawDatas),
      });

  const dataFrameSpec = dataFrames.length > 0 ? dataFrames[0] : null;
  // We need to update the draft step when the input step datas change, so we can use the new data in the preview.
  useEffect(() => {
    if (draftStep != null && !isEqual(draftStep.inputStepDatas, originalStep.inputStepDatas)) {
      setDraftStep({
        ...draftStep,
        inputStepDatas: originalStep.inputStepDatas,
      });
    }
  }, [draftStep, originalStep, setDraftStep]);

  // We need to update the draft step when the output step datas change for TABLE_VIEW draft steps, because TABLE_VIEW
  // draft steps use the output step datas for their preview.
  // This logic needs to run when there is a draft step and when an original step has finished updating after
  // adding a filter/transform step.
  // This will not wipe out previews for chart views, because chart views do not write output to the draft step.
  useEffect(() => {
    if (
      draftStep != null &&
      !isEqual(draftStep.outputStepDatas, originalStep.outputStepDatas) &&
      originalStep.statusCode === PipelineStepStatusCode.SUCCEEDED
    ) {
      setDraftStep({
        ...draftStep,
        outputStepDatas: originalStep.outputStepDatas,
      });
    }
  }, [draftStep, originalStep, setDraftStep]);

  const contextValue = useMemo(
    () => ({
      dataFramesInfos: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]:
          dataFrameSpec != null ? [{dataFrameSpec, id: dataFrameSpec.id}] : [],
      },
      clientStateId,
    }),
    [dataFrameSpec, clientStateId]
  );

  const confirmationButtons: Array<RightSidebarConfirmationButton> = [
    {
      text: 'Cancel',
      onClick: (): void => {
        onChangeSelectedRightSidebarItem(null);
        draftStepGlobalState.deleteState(originalStep.id);
      },
      isPrimary: originalStep.stepType !== PipelineStepType.CHART_VIEW,
      dataTestAttrsElement: 'cancelButton',
    },
  ];
  if (
    originalStep.stepType === PipelineStepType.CHART_VIEW ||
    originalStep.stepType === PipelineStepType.TABLE_VIEW
  ) {
    confirmationButtons.push({
      text: 'Save',
      isPrimary: true,
      onClick: async (): Promise<void> => {
        const draftStepType = draftStep?.stepType;
        const hasStepTypeChanged = draftStepType != null && draftStepType !== originalStep.stepType;

        if (hasStepTypeChanged && dataFrameSpec != null) {
          // Step type changed via chart type switcher (e.g. chart→table or table→chart)
          await withLoadingScreen(
            saveChartOrTable({
              analysis,
              originalChartOrTableStep: originalStep as PipelineStep<
                PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW
              >,
              newChartConfig:
                draftStepType === PipelineStepType.CHART_VIEW
                  ? (draftStep?.config as PipelineStepConfig<PipelineStepType.CHART_VIEW, 0> | undefined)
                  : undefined,
              newStepType: draftStepType as PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
              inputDataFrameId: dataFrameSpec.id,
              onChangeSelectedRightSidebarItem,
              onChangeSelectedItem,
              selectedRightSidebarItem,
            }),
            'Saving changes...'
          );
        } else if (
          originalStep.stepType === PipelineStepType.CHART_VIEW ||
          draftStep?.stepType === PipelineStepType.CHART_VIEW
        ) {
          // Chart view config change (same step type)
          await withLoadingScreen(
            PipelineDAO.updateAndRunPipelineStep(analysis.id, {
              stepId: originalStep.id,
              stepConfig: draftStep?.config ?? {},
            })
          );
        }
        onChangeSelectedRightSidebarItem(null);
        draftStepGlobalState.deleteState(originalStep.id);
      },
      disabled: isDisabled,
      dataTestAttrsElement: 'saveButton',
    });
  }

  const step = draftStep != null && draftStep.config != null ? draftStep : originalStep;
  return (
    <RightSidebarSkeleton
      title={getTitleForView(view, analysis.steps, stepTypeInfos)}
      width={RightSidebarWidth.STANDARD}
      confirmationButtons={confirmationButtons}
      dataTestAttrs={{
        component: 'EditViewRightSidebar',
        element: 'root',
      }}
    >
      <StepConfigEditorContext.Provider value={contextValue}>
        <EditViewRawData
          analysis={analysis}
          clientStateId={clientStateId}
          step={step}
          originalStep={originalStep}
          view={view}
          isDisabled={isDisabled}
          setDraftStep={setDraftStep}
        />
        <EditViewTransforms analysis={analysis} view={view} dataFrameSpec={dataFrameSpec} />
      </StepConfigEditorContext.Provider>
    </RightSidebarSkeleton>
  );
}

interface EditViewRawDataProps {
  analysis: PipelineGraphQL;
  clientStateId: string;
  step: PipelineStep;
  originalStep: PipelineStep;
  view: PipelineViewGraphQL;
  isDisabled: boolean;
  setDraftStep: (step: PipelineStep) => void;
}

/**
 * Returns the dataFrameColumns for the terminal step of a view, if available.
 */
function getDataFrameColumnsForStep(step: PipelineStep): DataFrameColumns | null {
  if (step.stepType === PipelineStepType.CHART_VIEW || step.stepType === PipelineStepType.TABLE_VIEW) {
    try {
      const dataFrame = getSingleInputRawData<
        PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW,
        0,
        PipelineStepDataKind.DATASET
      >(step as PipelineStep<PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW, 0>);
      return dataFrame.columns;
    } catch (err) {
      // getSingleInputRawData asserts when input raw data is empty (e.g. "no data" views).
      // Return null so callers degrade gracefully instead of crashing before the early return.
      if (err instanceof Error && err.message.includes('has no raw output datas')) {
        return null;
      }
      throw err;
    }
  }
  return null;
}

/** @package */
export function EditViewRawData({
  analysis,
  clientStateId,
  step,
  originalStep,
  view,
  isDisabled,
  setDraftStep,
}: EditViewRawDataProps): JSX.Element | null {
  const lastNode = view.lineage[view.lineage.length - 1];

  const dataFrameColumns = getDataFrameColumnsForStep(step) ?? getDataFrameColumnsForStep(originalStep);

  const handleSwitchToTable = useCallback((): void => {
    const name = pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalStep)
      ? originalStep.config?.chartName
      : pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, originalStep)
        ? originalStep.config?.viewName
        : undefined;
    setDraftStep({
      ...originalStep,
      stepType: PipelineStepType.TABLE_VIEW,
      config: {
        viewName: name,
      },
    });
  }, [originalStep, setDraftStep]);

  const handleSwitchToChart = useCallback(
    (newType: AnalysisVisualizationType): void => {
      if (dataFrameColumns == null) {
        return;
      }
      const chartOptionType =
        ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE[
          newType as Exclude<
            AnalysisVisualizationType,
            AnalysisVisualizationType.TABLE | AnalysisVisualizationType.CONTOUR_PLOT
          >
        ];
      const defaultConfig = getDefaultChartConfig(chartOptionType, dataFrameColumns);
      const name = pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, originalStep)
        ? originalStep.config?.viewName
        : pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalStep)
          ? originalStep.config?.chartName
          : undefined;
      setDraftStep({
        ...originalStep,
        stepType: PipelineStepType.CHART_VIEW,
        config: {
          ...defaultConfig,
          chartName: name ?? defaultConfig.chartName,
        },
      });
    },
    [originalStep, setDraftStep, dataFrameColumns]
  );

  if (lastNode.stepOutputReference == null) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="This view has no data." />;
  }

  // TABLE_VIEW with chart type switcher
  if (step.stepType === PipelineStepType.TABLE_VIEW) {
    return (
      <ChartTypeSwitcher
        currentType={AnalysisVisualizationType.TABLE}
        onChange={(newType) => {
          if (newType !== AnalysisVisualizationType.TABLE) {
            handleSwitchToChart(newType);
          }
        }}
        isDisabled={isDisabled}
      />
    );
  }

  if (step.stepType === PipelineStepType.CHART_VIEW) {
    assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, step);
    return (
      <EditViewChart
        analysis={analysis}
        clientStateId={clientStateId}
        step={step}
        view={view}
        isDisabled={isDisabled}
        dataFrameColumns={dataFrameColumns ?? []}
        onSwitchToTable={handleSwitchToTable}
      />
    );
  } else {
    return null;
  }
}
